import { useEffect } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUser } from "../../Actions/UserAction";
import { globalStyle } from "../../global/Utils";
import { CircularProgress } from "@mui/material";
import { appModeLabels } from "./Variables";
import moment from "moment";
import Swal from "sweetalert2";

const ViewUserDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("id");
  const { userDetailsList, isGettingUser } = useSelector(
    (state) => state.UserReducer,
  );
  const handleDangerVisibleForUserDetails = () => {
    Swal.fire({
      title: "Error",
      text: "User Not Found",
      icon: "error",
      timer: 30000,
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/main/view-user-list");
      }
    });
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getUser(userId, handleDangerVisibleForUserDetails));
    });
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [dispatch, userId]);
  const { userDetails, userLoginHistory, companyDetails } = userDetailsList;
  if (!userId) {
    return Navigate({ to: "/main/view-user-list" });
  }
  return (
    <>
      {isGettingUser ? (
        <div className="relative min-h-screen">
          <div className="absolute inset-0 flex items-center justify-center">
            <CircularProgress size={50} color="success" />
          </div>
        </div>
      ) : (
        <div
          className="p-6 animate-fade-in"
          style={{ fontFamily: globalStyle.fontFamily }}
        >
          <button
            onClick={() => navigate("/main/view-user-list")}
            className="mb-4 px-4 py-2 rounded text-white"
            style={{
              background: globalStyle.buttonColor,
              fontFamily: globalStyle.fontFamilyButton,
            }}
          >
            ← Back
          </button>
          <h2
            className="text-2xl font-bold mb-4 text-center"
            style={{
              color: globalStyle.blueColor,
              fontFamily: globalStyle.fontFamilyHeading,
            }}
          >
            User Profile
          </h2>
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {[
              { label: "Name", value: userDetails?.userName },
              { label: "Email", value: userDetails?.email },
              { label: "Role", value: userDetails?.role },
              { label: "Allow Companies", value: userDetails?.allowCompanies },
              { label: "Allow Invoices", value: userDetails?.allowInvoices },
              { label: "Subscription Interval", value: userDetails?.subscriptionInterval },
              { label: "App Mode", value: appModeLabels(userDetails?.appMode) },
              { label: "Last Login", value: userDetails?.lastLogin },
              {
                label: "Created At",
                value: moment(userDetails?.createdAt).format("LLL"),
              },
              { label: "Active Date", value: userDetails?.activeDate || "" },
            ].map((item, index) => (
              <div key={index}>
                <p className="font-semibold text-gray-600">{item.label}:</p>
                <p
                  className="text-base"
                  style={{ color: globalStyle.blueColor }}
                >
                  {item.value}
                </p>
              </div>
            ))}
          </div>

          {/* Login History */}
          <h3
            className="text-xl font-bold mt-8 mb-2"
            style={{ color: globalStyle.curveDarkColor }}
          >
            Login History
          </h3>
          <div
                      >
            {userLoginHistory && userLoginHistory.length > 0 ? (
              <>
                {/* Desktop Table */}
                <table className="hidden md:table w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border px-4 py-2 text-left text-gray-700">
                        Date
                      </th>
                      <th className="border px-4 py-2 text-left text-gray-700">
                        Attempts to Login
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {userLoginHistory?.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border px-4 py-2">{item.date}</td>
                        <td className="border px-4 py-2 text-blue-600 font-semibold">
                          {item.attempt}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile Cards */}
                <div className="block md:hidden space-y-3">
                  {userLoginHistory?.map((item, index) => (
                    <div
                      key={index}
                      className="rounded-lg border overflow-hidden"
                      style={{ borderColor: globalStyle.curveLightColor }}
                    >
                      {/* Card Header */}
                      <div
                        className="px-4 py-2"
                        style={{ background: globalStyle.curveLightColor }}
                      >
                        <span
                          className="text-sm font-semibold"
                          style={{ color: globalStyle.curveDarkColor }}
                        >
                          Entry #{index + 1}
                        </span>
                      </div>
                      {/* Card Body */}
                      <div className="bg-gray-50 divide-y divide-gray-200">
                        <div className="flex justify-between items-center px-4 py-3">
                          <span className="text-sm font-medium text-gray-500">
                            Date
                          </span>
                          <span className="text-sm font-semibold text-gray-800">
                            {item.date}
                          </span>
                        </div>
                        <div className="flex justify-between items-center px-4 py-3">
                          <span className="text-sm font-medium text-gray-500">
                            Attempts to Login
                          </span>
                          <span className="text-sm font-bold text-blue-600">
                            {item.attempt}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-gray-500">No Login History available.</p>
            )}
          </div>

          {/* Companies Details */}
          <h3
            className="text-xl font-bold mt-8 mb-2"
            style={{ color: globalStyle.curveDarkColor }}
          >
            Companies Details
          </h3>
          <div
                      >
            {companyDetails && companyDetails.length > 0 ? (
              <>
                {/* Desktop Table */}
                <table className="hidden md:table w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border px-4 py-2 text-left text-gray-700">
                        Company Name
                      </th>
                      <th className="border px-4 py-2 text-left text-gray-700">
                        NTN
                      </th>
                      <th className="border px-4 py-2 text-left text-gray-700">
                        Company Status
                      </th>
                      <th className="border px-4 py-2 text-left text-gray-700">
                        Integration Status
                      </th>
                      <th className="border px-4 py-2 text-left text-gray-700">
                        Production Token
                      </th>
                      <th className="border px-4 py-2 text-left text-gray-700">
                        SandBox Token
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyDetails?.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border px-4 py-2">
                          {item.companyName || "-"}
                        </td>
                        <td className="border px-4 py-2">{item.ntn || "-"}</td>
                        <td className="border px-4 py-2">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-semibold text-white ${item.companyStatus === "Sandbox" ? "bg-orange-500" : item.companyStatus === "Production" ? "bg-green-500" : "bg-gray-300 text-black"}`}
                          >
                            {item.companyStatus || "-"}
                          </span>
                        </td>
                        <td className="border px-4 py-2">
                          {item.integrationStatus || "N/A"}
                        </td>
                        <td className="border px-4 py-2">
                          {item.productiontoken || "N/A"}
                        </td>
                        <td className="border px-4 py-2">
                          {item.sandBoxToken || "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile Cards */}
                <div className="block md:hidden space-y-4">
                  {companyDetails?.map((item, index) => (
                    <div
                      key={index}
                      className="rounded-lg border overflow-hidden"
                      style={{ borderColor: globalStyle.curveLightColor }}
                    >
                      {/* Card Header */}
                      <div
                        className="flex items-center justify-between px-4 py-3"
                        style={{ background: globalStyle.curveLightColor }}
                      >
                        <span
                          className="text-sm font-bold truncate mr-2"
                          style={{ color: globalStyle.curveDarkColor }}
                        >
                          {item.companyName || "-"}
                        </span>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold text-white flex-shrink-0 ${
                            item.companyStatus === "Sandbox"
                              ? "bg-orange-500"
                              : item.companyStatus === "Production"
                                ? "bg-green-500"
                                : "bg-gray-300 text-black"
                          }`}
                        >
                          {item.companyStatus || "-"}
                        </span>
                      </div>

                      {/* Card Body */}
                      <div className="bg-gray-50 divide-y divide-gray-200">
                        <div className="flex justify-between items-center px-4 py-3">
                          <span className="text-sm font-medium text-gray-500">
                            NTN
                          </span>
                          <span className="text-sm font-semibold text-gray-800">
                            {item.ntn || "-"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center px-4 py-3">
                          <span className="text-sm font-medium text-gray-500">
                            Integration Status
                          </span>
                          <span className="text-sm font-semibold text-gray-800">
                            {item.integrationStatus || "N/A"}
                          </span>
                        </div>
                        <div className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-500 mb-1">
                            Production Token
                          </p>
                          <p className="text-sm font-semibold text-gray-800 break-all">
                            {item.productiontoken || "N/A"}
                          </p>
                        </div>
                        <div className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-500 mb-1">
                            SandBox Token
                          </p>
                          <p className="text-sm font-semibold text-gray-800 break-all">
                            {item.sandBoxToken || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-gray-500">No Companies Details available.</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ViewUserDetails;
